/**
 * 보상 획득 모달 프레젠터 (일감 S3c) — GameScene openRewardModal/openRewardDetail/toRewardCell/buildRewardDetail 추출.
 * 순수 변환(셀/상세 모델 — 여기서만 t()/formatBig 적용)은 모듈 함수, 모달 결선만 클래스 (Humble View).
 * 던전/일일상점/골드 상자 보상 지급 직후 호출 — 이미 열려 있으면(소탕 연타) 새 모달 대신 내용만 갱신.
 */
import { t } from "../i18n/i18n.js";
import { formatBig } from "./formatNumber.js";
import {
  buildRewardModalItems,
  type RewardLineItem,
  type RewardShape,
} from "./rewardModalData.js";
import { REWARD_ICON_KEY, rewardTint } from "./rewardIcons.js";
import type { RewardCellModel } from "./RewardAcquisitionModal.js";
import type { RewardDetailModel } from "./RewardDetailModal.js";
import type { ResolvedReward } from "../systems/RewardResolver.js";
import { ALL_MODULE_DEFINITIONS } from "../data/moduleDefinitions.js";
import type { ModalHost } from "./ModalHost.js";

/** RewardLineItem → 셀 모델 (순수 — 표시 문구/아이콘/틴트 결정). */
export function toRewardCell(it: RewardLineItem): RewardCellModel {
  const displayName =
    it.kind === "module" && it.id
      ? t(`module.${it.id}.name`)
      : t(`reward.name.${it.kind}`);
  let amountText: string;
  switch (it.kind) {
    case "gold":
    case "diamond":
      amountText = it.amountBig ? formatBig(it.amountBig) : "";
      break;
    case "module":
    case "artifact":
      amountText = t("reward.module.count", { count: it.amount });
      break;
    default:
      amountText = String(it.amount);
  }
  return {
    item: it,
    displayName,
    amountText,
    iconKey: REWARD_ICON_KEY[it.kind],
    tint: rewardTint(it),
    shape: it.shape,
  };
}

/** RewardLineItem → 상세 모델 (순수 — 모듈=등급/슬롯/설명, 재화=설명문). */
export function buildRewardDetail(it: RewardLineItem): RewardDetailModel {
  const iconKey = REWARD_ICON_KEY[it.kind];
  const tint = rewardTint(it);
  if (it.kind === "module" && it.id) {
    const def = ALL_MODULE_DEFINITIONS.find((d) => d.id === it.id);
    return {
      title: t(`module.${it.id}.name`),
      tint,
      iconKey,
      shape: it.shape,
      lines: [
        t(`rarity.${it.rarity ?? "common"}`),
        t("reward.detail.slot", {
          slot: def ? t(`slot.${def.slotType}`) : "",
        }),
        t(`module.${it.id}.desc`),
      ],
    };
  }
  return {
    title: t(`reward.name.${it.kind}`),
    tint,
    iconKey,
    lines: [t(`reward.detail.${it.kind}`)],
  };
}

export interface RewardModalPresenterDeps {
  modals: ModalHost;
  /** 모달 배치 기준 화면 크기 — scene.scale 위임 getter. */
  screen: () => { width: number; height: number };
}

export class RewardModalPresenter {
  constructor(private readonly deps: RewardModalPresenterDeps) {}

  /**
   * 보상 지급 직후 호출 — ResolvedReward를 그리드 팝업으로 표시(일일상점/던전/골드 상자 연결). 빈 보상은 생략.
   * 이미 열려 있으면(던전 소탕 연타) 새 모달 대신 setCells로 내용만 갱신.
   * 파츠 1개 단독 획득(골드 상자 등)은 그리드를 생략하고 곧장 파츠 상세 팝업(+획득 헤더)을 연다.
   *
   * @param r 지급할 보상.
   * @param instances RewardService.grant 반환값 — drop 순서대로 생성된 ModuleInstance[]에서 추출한
   *   RewardShape 배열. 생략(기본값 [])하면 모든 모듈 셀이 획일 아이콘으로 폴백된다.
   */
  open(r: ResolvedReward, instances: readonly RewardShape[] = []): void {
    const { modals } = this.deps;
    const items = buildRewardModalItems(r, instances);
    if (items.length === 0) return;
    const cells = items.map((it) => toRewardCell(it));
    if (modals.hasReward()) {
      modals.setRewardCells(cells);
      return;
    }
    if (items.length === 1 && items[0].kind === "module") {
      this.openDetail(items[0], t("reward.popup.title"));
      return;
    }
    const screen = this.deps.screen();
    modals.openReward({
      screenWidth: screen.width,
      screenHeight: screen.height,
      cells,
      onSelectItem: (it) => this.openDetail(it),
      onClose: () => modals.closeReward(),
    });
  }

  /** 보상 요소 클릭 → 상세 팝업 (획득 팝업 위 depth, 닫으면 획득 팝업으로 복귀). 직행 시 headerTitle 표기. */
  private openDetail(it: RewardLineItem, headerTitle?: string): void {
    const { modals } = this.deps;
    const screen = this.deps.screen();
    modals.openRewardDetail({
      screenWidth: screen.width,
      screenHeight: screen.height,
      model: buildRewardDetail(it),
      headerTitle,
      onClose: () => modals.closeRewardDetail(),
    });
  }
}
